/**
 * Per-upload detail viewer: stored summary, error and warning rows, and
 * original-file redownload. Uploads from before file retention shipped
 * show an honest "file not retained" state instead of a download.
 */
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Button,
  Chip,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { StatCard, LoadingSkeleton } from '../common';
import { IssueTable, CapCaption, uploadStatusChipSx } from './uploadUi';
import { useUploadDetail } from '../../services/queries';
import { downloadUploadFile } from '../../services/admin';
import { useThemeMode } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate } from '../../utils/format';

function UploadDetailDialog({ uploadId, onClose }) {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  // Keep rendering the last upload during the close animation so the
  // content does not flash to the empty fallback.
  const lastIdRef = useRef(null);
  if (uploadId) lastIdRef.current = uploadId;
  const effectiveId = uploadId || lastIdRef.current;
  const { data, isLoading, isError } = useUploadDetail(effectiveId);
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloadError('');
    setDownloading(true);
    try {
      await downloadUploadFile(uploadId, data?.filename);
    } catch (err) {
      setDownloadError(err?.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    setDownloadError('');
    onClose();
  };

  const errors = data?.errorDetails || [];
  const warnings = data?.warningDetails || [];

  return (
    <Dialog
      open={!!uploadId}
      onClose={handleClose}
      fullWidth
      PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 640, width: '100%' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Icon icon="ph:file-xls" width={22} height={22} color={palette.textSecondary} />
          <Typography variant="h6" component="span" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {data?.filename || 'Upload detail'}
          </Typography>
          {data?.status && <Chip size="small" label={data.status} sx={uploadStatusChipSx(palette, data.status)} />}
        </Box>
        {data && (
          <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mt: 0.5 }}>
            {fmtDate(data.uploadDate)}
            {data.uploadedBy ? ` · ${data.uploadedBy}` : ''}
            {data.mode ? ` · ${data.mode === 'bulk' ? 'bulk import' : 'weekly upload'}` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <LoadingSkeleton variant="table" rows={3} />}
        {isError && <Alert severity="error">Could not load this upload's details.</Alert>}
        {data && (
          <Box>
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <StatCard label="Processed" value={(data.recordsProcessed ?? 0).toLocaleString()} tone="info" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard label="Inserted" value={(data.inserted ?? 0).toLocaleString()} tone="accent" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard label="Updated" value={(data.updated ?? 0).toLocaleString()} tone="teal" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard label="Errors" value={(data.errors ?? 0).toLocaleString()} tone={data.errors > 0 ? 'danger' : 'neutral'} />
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mt: 1.5 }}>
              {(data.skipped ?? 0).toLocaleString()} skipped (duplicates / filtered) · {(data.warnings ?? 0).toLocaleString()} warnings
            </Typography>

            {errors.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="subtitle2" gutterBottom>Errors</Typography>
                <IssueTable rows={errors} tone="danger" maxHeight={220} />
                <CapCaption shown={errors.length} total={data.errors} noun="errors" />
              </Box>
            )}
            {warnings.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="subtitle2" gutterBottom>Warnings</Typography>
                <IssueTable rows={warnings} tone="warning" maxHeight={220} />
                <CapCaption shown={warnings.length} total={data.warnings} noun="warnings" />
              </Box>
            )}
            {downloadError && (
              <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setDownloadError('')}>
                {downloadError}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Tooltip
          title={data && !data.fileRetained ? 'File not retained (uploaded before file retention shipped)' : ''}
        >
          <span>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Icon icon="ph:download-simple" width={16} height={16} />}
              onClick={handleDownload}
              disabled={!data?.fileRetained || downloading}
              sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
            >
              {downloading ? 'Downloading…' : 'Download original file'}
            </Button>
          </span>
        </Tooltip>
        <Button onClick={handleClose} sx={{ color: palette.textMuted, textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UploadDetailDialog;
